<template>
  <header id="header-mobile" class="fixed top-0 w-full z-100 lg:hidden">
    <nav>
      <div class="navigation-mobile-nav-bar">
        <button class="pr-24 pl-16 accessible-border-mobile" @click="openMobileNav">
          <span class="sr-only">Open main navigation</span>
          <svg v-if="!mobileNavOpen" width="18" height="20" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <title>Open main navigation</title>
            <path
              d="M1 2C0.447715 2 0 1.55228 0 1C0 0.447715 0.447715 0 1 0H17C17.5523 0 18 0.447715 18 1C18 1.55228 17.5523 2 17 2H1Z"
              fill="#FF0064"
            />
            <path
              d="M1 8C0.447715 8 0 7.55228 0 7C0 6.44772 0.447715 6 1 6H17C17.5523 6 18 6.44772 18 7C18 7.55228 17.5523 8 17 8H1Z"
              fill="#FF0064"
            />
            <path
              d="M0 13C0 13.5523 0.447715 14 1 14H17C17.5523 14 18 13.5523 18 13C18 12.4477 17.5523 12 17 12H1C0.447715 12 0 12.4477 0 13Z"
              fill="#FF0064"
            />
          </svg>
          <svg v-else width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <title>Close main navigation</title>
            <path
              d="M0.292893 15.7071C0.683418 16.0976 1.31658 16.0976 1.70711 15.7071L8 9.41421L14.2929 15.7071C14.6834 16.0976 15.3166 16.0976 15.7071 15.7071C16.0976 15.3166 16.0976 14.6834 15.7071 14.2929L9.41421 8L15.7071 1.70711C16.0976 1.31658 16.0976 0.683417 15.7071 0.292893C15.3166 -0.0976311 14.6834 -0.0976311 14.2929 0.292893L8 6.58579L1.70711 0.292893C1.31658 -0.0976311 0.683418 -0.0976311 0.292893 0.292893C-0.0976309 0.683417 -0.0976309 1.31658 0.292893 1.70711L6.58579 8L0.292893 14.2929C-0.0976311 14.6834 -0.0976311 15.3166 0.292893 15.7071Z"
              fill="#FF0064"
            />
          </svg>
        </button>

        <slot name="logo">
          <a href="/" class="accessible-border-mobile" @click="closeMobileDrawerIfOpen">
            <img src="@/images/components/header/sittercity-mark-logo.svg" alt="Sittercity" class="w-36" />
          </a>
        </slot>

        <router-link
          to="/messages"
          class="flex items-center ml-auto pr-16 accessible-border-mobile relative"
          @click.native="closeMobileDrawerIfOpen"
        >
          <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <title>Messages</title>
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M2 0H18C19.1046 0 20 0.89543 20 2V14C20 15.1046 19.1046 16 18 16H2C0.89543 16 0 15.1046 0 14V2C0 0.89543 0.89543 0 2 0ZM2 3.37073V14H18V3.37073L10 10.8707L2 3.37073ZM3.46211 2L10 8.12927L16.5379 2H3.46211Z"
              fill="#4B4C52"
            />
          </svg>
          <span
            v-if="unreadMessageCount > 0"
            class="nav-unread-messages-count bg-brand-contrast text-center text-foreground font-semibold inline-block h-20 w-20 rounded-full text-xs tracking-tighter leading-5 ml-4"
            >{{ unreadMessageCount }}</span
          >
        </router-link>
      </div>

      <div v-if="mobileNavOpen" class="bg-foreground fixed pt-48 h-screen w-full z-100">
        <slot name="drawer-extra" />
        <ul class="pt-40 pl-64">
          <li v-for="(link, index) in links" :key="index" class="pb-20">
            <router-link
              v-if="isRouterPath(link.href)"
              :to="link.href"
              class="text-grey-text-subdued text-m1 link--tertiary"
              @click.native="closeMobileDrawerIfOpen"
            >
              {{ link.label }}
            </router-link>
            <a
              v-else
              :href="link.href"
              class="text-grey-text-subdued text-m1 link--tertiary"
              :rel="link.external ? 'external' : undefined"
              @click="closeMobileDrawerIfOpen"
            >
              {{ link.label }}
            </a>
            <span
              v-if="link.badgeCount != null && link.badgeCount > 0"
              class="nav-unread-messages-count bg-brand-contrast text-center text-foreground font-semibold inline-block h-20 w-20 rounded-full text-xs tracking-tighter leading-5 ml-4 relative -top-1"
              >{{ link.badgeCount }}</span
            >
          </li>
        </ul>
      </div>
    </nav>
  </header>
</template>

<script lang="ts">
import { PropType } from 'vue';

export interface NavigationMobileTopLink {
  href: string;
  label: string;
  badgeCount?: number;
  external?: boolean;
}

export default {
  name: 'NavigationMobileTop',
  props: {
    links: {
      type: Array as PropType<NavigationMobileTopLink[]>,
      required: true,
    },
  },
  data() {
    return {
      mobileNavOpen: false,
    };
  },
  computed: {
    unreadMessageCount(): number {
      return this.$store.getters['navigation/unreadMessageCount'];
    },
  },
  beforeDestroy() {
    document.body.classList.remove('overflow-hidden');
  },
  methods: {
    openMobileNav() {
      this.mobileNavOpen = !this.mobileNavOpen;
      document.body.classList.toggle('overflow-hidden', this.mobileNavOpen);
    },
    closeMobileDrawerIfOpen() {
      if (!this.mobileNavOpen) return;
      this.mobileNavOpen = false;
      document.body.classList.remove('overflow-hidden');
    },
    isRouterPath(href: string): boolean {
      if (!href.startsWith('/') || href.startsWith('//')) return false;
      try {
        const resolved = this.$router.resolve(href);
        const matched = resolved?.route?.matched;
        if (!matched?.length) return false;
        const last = matched[matched.length - 1];
        return last.name !== 'CatchAll' && last.path !== '*';
      } catch {
        return false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.navigation-mobile-nav-bar {
  @apply bg-foreground fixed flex h-48 items-center w-full z-101;
  box-shadow: 0 2px 12px rgba(215, 216, 219, 0.9);
}
</style>
